"use client";
import React, { useEffect, useRef, useState, useCallback } from "react";
import { Modal, Spin, Button, App } from "antd";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useCnssCards, useCnssDependents } from "../../../../consultation/hooks/useCnss";
import "./CnamBookModal.css";

/* ===================== SELECTOR ===================== */
const selectCurrentUser = (state) =>
  state.auth?.user ?? state.patientAuth?.user ?? state.doctorAuth?.user ?? null;

/* ===================== GEOMETRIE DU CARNET =====================
   Le carnet est dessine a taille reelle puis mis a l'echelle (CSS
   transform) pour tenir dans la modale, quelle que soit la largeur. */
const PAGE_WIDTH = 400; // largeur d'un volet (px, taille "papier")
const BOOK_WIDTH = PAGE_WIDTH * 2; // carnet ouvert
const MIN_SCALE = 0.42;
const MAX_SCALE = 1.18; // on autorise un peu plus grand que la taille "papier"

/* ===================== HELPERS ===================== */
function fillRef(ref, displayValue, isoValue = null) {
  if (!ref?.current) return;
  if (displayValue !== undefined && displayValue !== null) {
    ref.current.innerText = String(displayValue);
    // le contenu vient d'etre re-rempli depuis l'API : l'ancienne saisie
    // mise en cache par bindEditable n'est plus valable
    delete ref.current.dataset.value;
  }
  if (isoValue !== null && isoValue !== undefined) ref.current.dataset.iso = String(isoValue);
  else delete ref.current.dataset.iso;
}

function readRef(ref) {
  if (!ref?.current) return "";

  const v = ref.current.dataset.value;
  if (v && v.trim()) return v.trim();

  const iso = ref.current.dataset.iso;
  if (iso && iso.trim()) return iso.trim();

  const txt = ref.current.textContent ?? "";
  return txt.trim();
}

// function bindEditable(ref) {
//   return {
//     ref,
//     contentEditable: true,
//     suppressContentEditableWarning: true,
//     onInput: (e) => {
//       e.currentTarget.dataset.value = (e.currentTarget.textContent ?? "").trim();
//     },
//     onBlur: (e) => {
//       e.currentTarget.dataset.value = (e.currentTarget.textContent ?? "").trim();
//     },
//   };
// }

function bindEditable(ref, readOnly = false) {
  return {
    ref,
    contentEditable: !readOnly,
    suppressContentEditableWarning: true,

    onInput: readOnly
      ? undefined
      : (e) => {
          e.currentTarget.dataset.value = (
            e.currentTarget.textContent ?? ""
          ).trim();
        },

    onBlur: readOnly
      ? undefined
      : (e) => {
          e.currentTarget.dataset.value = (
            e.currentTarget.textContent ?? ""
          ).trim();
        },
  };
}

function formatDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    const dd = String(d.getUTCDate()).padStart(2, "0");
    const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
    const yyyy = d.getUTCFullYear();
    return `${dd}/${mm}/${yyyy}`;
  } catch {
    return "";
  }
}

function toIsoDate(value) {
  if (!value) return "";
  const str = String(value).trim();

  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return str;

  let m = str.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (m) return `${m[3]}-${m[2].padStart(2, "0")}-${m[1].padStart(2, "0")}`;

  m = str.match(/^(\d{1,2})-(\d{1,2})-(\d{4})$/);
  if (m) return `${m[3]}-${m[2].padStart(2, "0")}-${m[1].padStart(2, "0")}`;

  return "";
}

function toIsoOnly(raw) {
  if (!raw) return null;
  try {
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return null;
    return d.toISOString().slice(0, 10);
  } catch {
    return null;
  }
}

function extractApiMessage(err) {
  if (!err) return "Erreur inconnue";
  if (Array.isArray(err?.message)) return err.message.join(" | ");
  if (typeof err?.message === "string") return err.message;
  if (typeof err?.error === "string") return err.error;
  return JSON.stringify(err);
}

/** CIN -> string | undefined */
function cinToStringOrUndef(raw) {
  const s = String(raw ?? "").trim();
  if (!s) return undefined;
  if (!/^\d+$/.test(s)) return undefined;
  return s;
}

/* ===================== DEP SLOT HELPERS ===================== */
const sortByCreatedAtAsc = (arr) =>
  [...(arr ?? [])].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));

const buildSlots = (existingDeps) => {
  const adjoints = sortByCreatedAtAsc((existingDeps ?? []).filter((d) => d.lienParente === "ADJOINT"));
  const kafalas = sortByCreatedAtAsc((existingDeps ?? []).filter((d) => d.lienParente === "KAFALA"));
  const enfants = sortByCreatedAtAsc((existingDeps ?? []).filter((d) => d.lienParente === "ENFANT"));

  const slots = {
    ADJOINT0: adjoints[0] ?? null,
    KAFALA0: kafalas[0] ?? null,
    KAFALA1: kafalas[1] ?? null,
  };

  for (let i = 0; i < 6; i++) {
    slots[`ENFANT${i}`] = enfants[i] ?? null;
  }
  return slots;
};

/* ===================== COMPONENT ===================== */
export default function CnamBookModal({
  open,
  onClose,
  cardId: cardIdProp,
  userId: userIdProp,
  verifierId,
  onCardCreated,

   // Réutilisation côté médecin
  readOnly = false,
  embedded = false,
  initiallyOpen = false,
  hideSaveButton = false,
}) {
  const { notification } = App.useApp();
  const { t, i18n } = useTranslation("cnamBook");
  const isRTL = (i18n?.language ?? "").toLowerCase().startsWith("ar");

  const reduxUser = useSelector(selectCurrentUser);
  const userId = userIdProp ?? reduxUser?.id ?? null;

  const {
    currentCard,
    cards,
    isLoadingCard,
    isLoadingCards,
    isCreating,
    isUpdating,
    fetchById,
    fetchByPatient,
    clearCurrent,
    createByPatient,
    update,
    clearErr,
    clearOk,
  } = useCnssCards();

  const {
    dependents,
    isLoading: isLoadingDeps,
    isAdding: isAddingDep,
    add: addDep,
    update: updateDep,
    remove: removeDep,
    fetchByCard: fetchDepsByCard,
    clearErr: clearDepErr,
    clearOk: clearDepOk,
  } = useCnssDependents();

  // const [isOpenBook, setIsOpenBook] = useState(false);

  const [isOpenBook, setIsOpenBook] = useState(
  embedded ? true : initiallyOpen
);

  const [activeCardId, setActiveCardId] = useState(cardIdProp ?? null);

  /* ===================== RESPONSIVE (auto-fit du carnet) ===================== */
  const wrapRef = useRef(null);
  const sceneRef = useRef(null);
  const [activePage, setActivePage] = useState("holder"); // "holder" | "children"
  const [zoom, setZoom] = useState(1); // 1 = ajuste a la fenetre
  const [fit, setFit] = useState({ scale: 1, compact: false, naturalH: 0 });

  useEffect(() => {
    if (open) {
      setActivePage("holder");
      setZoom(1);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;

    let frame = 0;

    const measure = () => {
      const wrap = wrapRef.current;
      const scene = sceneRef.current;
      if (!wrap || !scene) return;

      const availableW = wrap.clientWidth - 24;
      if (availableW <= 0) return;

      /* Hauteur disponible : la modale n'est bornee que par sa max-height, et
         le wrapper prend la hauteur de son contenu. On deduit donc la place
         libre de la fenetre, moins l'habillage (en-tete, barre, pied). */
      const body = wrap.parentElement;
      const chrome = body
        ? Array.from(body.children)
            .filter((el) => el !== wrap && !el.classList.contains("cnam-loading-overlay"))
            .reduce((sum, el) => sum + el.offsetHeight, 0)
        : 0;
      const maxBody = Math.min(window.innerHeight * 0.9, 960);
      const availableH = maxBody - chrome - 30; // 30 = padding vertical du wrapper

      // sous ~62% de la largeur du carnet ouvert => un volet a la fois
      const compact = availableW < BOOK_WIDTH * 0.62;
      const design = compact ? PAGE_WIDTH : BOOK_WIDTH;
      const naturalH = scene.offsetHeight; // hauteur non transformee

      let raw = availableW / design;
      // en mode double page, on tient aussi compte de la hauteur dispo
      if (!compact && naturalH > 0 && availableH > 160) {
        raw = Math.min(raw, availableH / naturalH);
      }

      const scale = Math.min(MAX_SCALE, Math.max(MIN_SCALE, raw));

      setFit((prev) =>
        prev.compact === compact &&
        Math.abs(prev.scale - scale) < 0.004 &&
        Math.abs(prev.naturalH - naturalH) < 1
          ? prev
          : { scale, compact, naturalH }
      );
    };

    const schedule = () => {
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(measure);
    };

    schedule();

    const observer =
      typeof ResizeObserver !== "undefined" ? new ResizeObserver(schedule) : null;
    if (observer) {
      if (wrapRef.current) observer.observe(wrapRef.current);
      if (sceneRef.current) observer.observe(sceneRef.current);
    }
    window.addEventListener("resize", schedule);

    return () => {
      window.cancelAnimationFrame(frame);
      if (observer) observer.disconnect();
      window.removeEventListener("resize", schedule);
    };
  }, [open, isOpenBook, activePage]);

  const card = currentCard ?? (cards?.length > 0 ? cards[0] : null);
  const effectiveCardId = cardIdProp ?? activeCardId ?? card?.id ?? null;
  const isNewCard = !effectiveCardId;

  const depList = Array.isArray(dependents) ? dependents : [];

  const isLoading = isLoadingCard || isLoadingCards || isLoadingDeps;
  const saving = isCreating || isUpdating || isAddingDep;

  /* ===================== REFS (CHAMPS INCHANGÉS) ===================== */
  const refIdentifiant = useRef(null);
  const refNomPrenom = useRef(null);
  const refDateNaissance = useRef(null);
  const refCin = useRef(null);
  const refAdresse = useRef(null);
  const refCaisseName = useRef(null);
  const refValidFrom = useRef(null);
  const refValidTo = useRef(null);
  const refSignature = useRef(null);

  const refConjointNom = useRef(null);
  const refConjointDate = useRef(null);
  const refConjointCin = useRef(null);

  const refAsc0Nom = useRef(null);
  const refAsc0Date = useRef(null);
  const refAsc0Cin = useRef(null);
  const refAsc1Nom = useRef(null);
  const refAsc1Date = useRef(null);
  const refAsc1Cin = useRef(null);

  const refE0Nom = useRef(null);
  const refE0Date = useRef(null);
  const refE0Fin = useRef(null);
  const refE1Nom = useRef(null);
  const refE1Date = useRef(null);
  const refE1Fin = useRef(null);
  const refE2Nom = useRef(null);
  const refE2Date = useRef(null);
  const refE2Fin = useRef(null);
  const refE3Nom = useRef(null);
  const refE3Date = useRef(null);
  const refE3Fin = useRef(null);
  const refE4Nom = useRef(null);
  const refE4Date = useRef(null);
  const refE4Fin = useRef(null);
  const refE5Nom = useRef(null);
  const refE5Date = useRef(null);
  const refE5Fin = useRef(null);

  const refAsc = useRef([
    { nom: refAsc0Nom, date: refAsc0Date, cin: refAsc0Cin },
    { nom: refAsc1Nom, date: refAsc1Date, cin: refAsc1Cin },
  ]);

  const refEnfants = useRef([
    { nom: refE0Nom, date: refE0Date, fin: refE0Fin },
    { nom: refE1Nom, date: refE1Date, fin: refE1Fin },
    { nom: refE2Nom, date: refE2Date, fin: refE2Fin },
    { nom: refE3Nom, date: refE3Date, fin: refE3Fin },
    { nom: refE4Nom, date: refE4Date, fin: refE4Fin },
    { nom: refE5Nom, date: refE5Date, fin: refE5Fin },
  ]);

  /* ===================== RESET ===================== */
  const resetAllRefs = useCallback(() => {
    const all = [
      refIdentifiant,
      refNomPrenom,
      refDateNaissance,
      refCin,
      refAdresse,
      refCaisseName,
      refValidFrom,
      refValidTo,
      refSignature,
      refConjointNom,
      refConjointDate,
      refConjointCin,
      ...refAsc.current.flatMap((a) => [a.nom, a.date, a.cin]),
      ...refEnfants.current.flatMap((e) => [e.nom, e.date, e.fin]),
    ];
    all.forEach((r) => {
      if (r?.current) {
        r.current.innerText = "";
        delete r.current.dataset.iso;
        delete r.current.dataset.value;
      }
    });
  }, []);

  /* ===================== FILL BOOK ===================== */
  const fillBookFromCard = useCallback((cardData, deps = []) => {
    if (!cardData) return;

    fillRef(refIdentifiant, cardData.identifiantUnique ?? "");
    fillRef(refNomPrenom, cardData.nomPrenom ?? "");
    fillRef(refCin, cardData.cin ?? "");
    fillRef(refAdresse, cardData.adresse ?? "");
    fillRef(refCaisseName, cardData.caisseName ?? "");

    fillRef(refDateNaissance, formatDate(cardData.dateNaissance), toIsoOnly(cardData.dateNaissance));
    fillRef(refValidFrom, formatDate(cardData.validFrom), toIsoOnly(cardData.validFrom));
    fillRef(refValidTo, formatDate(cardData.validTo), toIsoOnly(cardData.validTo));
    fillRef(refSignature, formatDate(cardData.createdAt));

    const conjoint = deps.find((d) => d.lienParente === "ADJOINT") ?? null;
    const ascendants = deps.filter((d) => d.lienParente === "KAFALA");
    const enfants = deps.filter((d) => d.lienParente === "ENFANT");

    fillRef(refConjointNom, conjoint?.nomPrenom ?? "");
    fillRef(refConjointDate, formatDate(conjoint?.dateNaissance), toIsoOnly(conjoint?.dateNaissance));
    fillRef(refConjointCin, conjoint?.cin ?? "");

    refAsc.current.forEach((a) => {
      fillRef(a.nom, "");
      fillRef(a.date, "");
      fillRef(a.cin, "");
    });
    ascendants.slice(0, 2).forEach((a, i) => {
      fillRef(refAsc.current[i].nom, a.nomPrenom ?? "");
      fillRef(refAsc.current[i].date, formatDate(a.dateNaissance), toIsoOnly(a.dateNaissance));
      fillRef(refAsc.current[i].cin, a.cin ?? "");
    });

    refEnfants.current.forEach((e) => {
      fillRef(e.nom, "");
      fillRef(e.date, "");
      fillRef(e.fin, "");
    });
    enfants.slice(0, 6).forEach((e, i) => {
      fillRef(refEnfants.current[i].nom, e.nomPrenom ?? "");
      fillRef(refEnfants.current[i].date, formatDate(e.dateNaissance), toIsoOnly(e.dateNaissance));
      fillRef(refEnfants.current[i].fin, formatDate(e.dateExpiration), toIsoOnly(e.dateExpiration));
    });
  }, []);

  /* ===================== OPEN ===================== */
  useEffect(() => {
    // if (!open) return;
    // setIsOpenBook(false);

    if (!open && !embedded) return;

    setIsOpenBook(embedded ? true : initiallyOpen);

    clearCurrent();
    clearErr();
    clearOk();
    clearDepErr();
    clearDepOk();

    resetAllRefs();

    setActiveCardId((prev) => cardIdProp ?? prev ?? null);
    const idToLoad = cardIdProp ?? activeCardId ?? null;

    if (idToLoad) fetchById(idToLoad);
    else if (userId) fetchByPatient(userId);
  }, [open, embedded, initiallyOpen]); 

  /* ===================== CARD LOADED => FETCH DEPS ===================== */
  useEffect(() => {
    if (!card?.id) return;
    setActiveCardId(card.id);
    if (userId) fetchDepsByCard(card.id, userId);
  }, [card?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ===================== DEPS LOADED => FILL ===================== */
  useEffect(() => {
    if (!card) return;
    fillBookFromCard(card, depList);
  }, [card, depList.length, fillBookFromCard]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ===================== COLLECT CARD ===================== */
  const collectCardData = useCallback(
    () => ({
      identifiantUnique: readRef(refIdentifiant),
      nomPrenom: readRef(refNomPrenom),
      cin: readRef(refCin),
      adresse: readRef(refAdresse),
      caisseName: readRef(refCaisseName) || "CNSS",
      dateNaissance: toIsoDate(readRef(refDateNaissance)),
      validFrom: toIsoDate(readRef(refValidFrom)),
      validTo: toIsoDate(readRef(refValidTo)),
    }),
    []
  );

  /* ===================== COLLECT DEPENDENTS (SLOTS) ===================== */
  const collectDependentsSlots = useCallback(() => {
    const items = [];

    // ADJOINT
    const conjNom = (readRef(refConjointNom) || "").trim();
    if (conjNom) {
      const dateNaissance = toIsoDate(readRef(refConjointDate));
      if (!dateNaissance) {
        return { ok: false, reason: t("validation.spouseBirthdate"), items: [] };
      }
      const cin = cinToStringOrUndef(readRef(refConjointCin));

      items.push({
        slotKey: "ADJOINT0",
        dto: {
          lienParente: "ADJOINT",
          nomPrenom: conjNom,
          dateNaissance,
          ...(cin !== undefined ? { cin } : {}),
        },
      });
    }

    // KAFALA
    for (let i = 0; i < refAsc.current.length; i++) {
      const asc = refAsc.current[i];
      const nom = (readRef(asc.nom) || "").trim();
      if (!nom) continue;

      const dateNaissance = toIsoDate(readRef(asc.date));
      if (!dateNaissance) {
        return {
          ok: false,
          reason: t("validation.ascendantBirthdate", { index: i + 1 }),
          items: [],
        };
      }
      const cin = cinToStringOrUndef(readRef(asc.cin));

      items.push({
        slotKey: `KAFALA${i}`,
        dto: {
          lienParente: "KAFALA",
          nomPrenom: nom,
          dateNaissance,
          ...(cin !== undefined ? { cin } : {}),
        },
      });
    }

    // ENFANT — toujours ajout, puis update uniquement dateExpiration
    for (let i = 0; i < refEnfants.current.length; i++) {
      const e = refEnfants.current[i];
      const nom = (readRef(e.nom) || "").trim();
      if (!nom) continue;

      const dateNaissance = toIsoDate(readRef(e.date));
      const dateExpiration = toIsoDate(readRef(e.fin));

      if (!dateNaissance) {
        return { ok: false, reason: t("validation.childBirthdate", { index: i + 1 }), items: [] };
      }
      if (!dateExpiration) {
        return { ok: false, reason: t("validation.childExpiration", { index: i + 1 }), items: [] };
      }

      items.push({
        slotKey: `ENFANT${i}`,
        dto: {
          lienParente: "ENFANT",
          nomPrenom: nom,
          dateNaissance,
          dateExpiration,
        },
      });
    }

    return { ok: true, reason: "", items };
  }, [t]);

  /* ===================== UPSERT DEPENDENTS ===================== */
  const upsertDependents = useCallback(
    async (cardId) => {
      if (!cardId || !userId) return;

      const res = collectDependentsSlots();
      if (!res.ok) {
        notification.warning({
          message: t("notify.validationTitle"),
          description: res.reason,
          placement: "topRight",
        });
        return;
      }

      const existing = Array.isArray(dependents) ? dependents : [];
      const slots = buildSlots(existing);

      // UPSERT filled slots
      for (const { slotKey, dto } of res.items) {
        const existingDep = slots[slotKey];

        // ✅ ENFANT: si existe, PUT uniquement dateExpiration
        if (dto.lienParente === "ENFANT") {
          if (existingDep?.id) {
            const putDto = { dateExpiration: dto.dateExpiration };
            await updateDep(cardId, userId, existingDep.id, putDto).unwrap();
          } else {
            // POST complet à la première création
            const postDto = {
              lienParente: "ENFANT",
              nomPrenom: dto.nomPrenom,
              dateNaissance: dto.dateNaissance,
              dateExpiration: dto.dateExpiration,
            };
            await addDep(cardId, userId, postDto).unwrap();
          }
          continue;
        }

        // ADJOINT/KAFALA: POST/PUT normal
        const strictDto = {
          lienParente: dto.lienParente,
          nomPrenom: dto.nomPrenom,
          dateNaissance: dto.dateNaissance,
          ...(dto.cin ? { cin: dto.cin } : {}),
        };

        if (existingDep?.id) {
          await updateDep(cardId, userId, existingDep.id, strictDto).unwrap();
        } else {
          await addDep(cardId, userId, strictDto).unwrap();
        }
      }

      // DELETE cleared slots (si on vide un slot existant)
      const filledKeys = new Set(res.items.map((x) => x.slotKey));
      const allSlotKeys = [
        "ADJOINT0",
        "KAFALA0",
        "KAFALA1",
        "ENFANT0",
        "ENFANT1",
        "ENFANT2",
        "ENFANT3",
        "ENFANT4",
        "ENFANT5",
      ];

      for (const slotKey of allSlotKeys) {
        const existingDep = slots[slotKey];
        if (existingDep?.id && !filledKeys.has(slotKey)) {
          await removeDep(cardId, userId, existingDep.id).unwrap();
        }
      }

      await fetchDepsByCard(cardId, userId);
    },
    [
      userId,
      dependents,
      collectDependentsSlots,
      addDep,
      updateDep,
      removeDep,
      fetchDepsByCard,
      notification,
      t,
    ]
  );

  /* ===================== SAVE BUTTON ===================== */
  const handleSave = useCallback(async () => {
    const cardPayload = collectCardData();
    let savedCardId = effectiveCardId;

    try {
      if (effectiveCardId) {
        const { identifiantUnique, ...updatePayload } = cardPayload;
        await update(effectiveCardId, verifierId ?? userId, updatePayload).unwrap();
      } else {
        if (!userId) throw new Error(t("notify.missingUser"));
        const created = await createByPatient(userId, cardPayload).unwrap();
        savedCardId = created?.id;
        if (savedCardId) {
          setActiveCardId(savedCardId);
          onCardCreated?.(created);
        }
      }

      if (!savedCardId) {
        notification.error({
          message: t("notify.errorTitle"),
          description: t("notify.noCardId"),
          placement: "topRight",
        });
        return;
      }

      await upsertDependents(savedCardId);

      await fetchById(savedCardId);
      if (userId) await fetchDepsByCard(savedCardId, userId);

      notification.success({
        message: t("notify.successTitle"),
        description: t("notify.successDesc"),
        placement: "topRight",
      });
    } catch (err) {
      notification.error({
        message: t("notify.errorTitle"),
        description: extractApiMessage(err) || t("notify.unknownError"),
        placement: "topRight",
        duration: 8,
      });
    }
  }, [
    effectiveCardId,
    userId,
    verifierId,
    collectCardData,
    update,
    createByPatient,
    upsertDependents,
    fetchById,
    fetchDepsByCard,
    onCardCreated,
    notification,
    t,
  ]);

  /* ===================== UI ===================== */
  const designWidth = fit.compact ? PAGE_WIDTH : BOOK_WIDTH;
  const scale = fit.scale * zoom;
  const hostStyle = fit.naturalH
    ? { width: `${designWidth * scale}px`, height: `${fit.naturalH * scale}px` }
    : undefined;

  const bookVars = {
    "--cnam-scale": String(scale),
    "--cnam-book-w": `${designWidth}px`,
  };

  const showHolder = !fit.compact || activePage === "holder";
  const showChildren = !fit.compact || activePage === "children";

  const zoomPercent = Math.round(scale * 100);
  const setZoomClamped = (next) => setZoom(Math.min(2.5, Math.max(0.5, next)));

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      centered
      width={940}
      zIndex={10000}
      className="cnam-real-modal"
      title={null}
      maskClosable={false}
      closable={false}
    >
      <div className="cnam-modal-body" dir={isRTL ? "rtl" : "ltr"}>
        {isLoading && (
          <div className="cnam-loading-overlay">
            <Spin size="large" tip={t("loading")} />
          </div>
        )}

        {/* ---------- EN-TÊTE ---------- */}
        <div className="cnam-modal-head">
          <div className="cnam-modal-head__text">
            <h2 className="cnam-modal-head__title">{t("head.title")}</h2>
          </div>

          <button
            type="button"
            className="cnam-modal-head__close"
            onClick={onClose}
            aria-label={t("actions.close")}
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path
                d="M6 6l12 12M18 6L6 18"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        {/* ---------- BARRE D'OUTILS ---------- */}
        <div className="cnam-toolbar">
          {isOpenBook && fit.compact ? (
            <div className="cnam-pager" role="tablist" aria-label={t("aria.pages")}>
              <button
                type="button"
                role="tab"
                aria-selected={activePage === "holder"}
                className={`cnam-pager__btn ${activePage === "holder" ? "is-active" : ""}`}
                onClick={() => setActivePage("holder")}
              >
                {t("tabs.holder")}
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={activePage === "children"}
                className={`cnam-pager__btn ${activePage === "children" ? "is-active" : ""}`}
                onClick={() => setActivePage("children")}
              >
                {t("tabs.children")}
              </button>
            </div>
          ) : null}

          <div className="cnam-zoom" role="group" aria-label={t("aria.zoom")}>
            <button
              type="button"
              className="cnam-zoom__btn"
              onClick={() => setZoomClamped(zoom - 0.15)}
              disabled={zoom <= 0.5}
              aria-label={t("zoom.out")}
            >
              −
            </button>
            <button
              type="button"
              className="cnam-zoom__value"
              onClick={() => setZoom(1)}
              title={t("zoom.fit")}
            >
              {zoomPercent}%
            </button>
            <button
              type="button"
              className="cnam-zoom__btn"
              onClick={() => setZoomClamped(zoom + 0.15)}
              disabled={zoom >= 2.5}
              aria-label={t("zoom.in")}
            >
              +
            </button>
          </div>
        </div>

        {/* ---------- CARNET ---------- */}
        <div className="cnam-book-wrapper" ref={wrapRef}>
          <div className="cnam-scale-host" style={hostStyle}>
            <div
              ref={sceneRef}
              className={`cnam-book-scene ${isOpenBook ? "is-open" : ""}`}
              style={bookVars}
            >
              {/* ----- COUVERTURE (recto de la carte de soins) ----- */}
              <button
                type="button"
                className="cnam-cover"
                onClick={() => setIsOpenBook(true)}
                aria-label={t("cover.open")}
              >
                <span className="cnam-cover__frame" aria-hidden="true" />

                <span className="cnam-cover__sheet">
                  {/* volet identité CNAM */}
                  <span className="cnam-cover__panel cnam-cover__panel--brand">
                    <span className="cnam-cover__ministry">
                      <i>الجمهورية التونسية</i>
                      <i>وزارة الشؤون الاجتماعية</i>
                      <i>والتضامن والتونسيين بالخارج</i>
                    </span>

                    <span className="cnam-cover__logo">
                      <svg className="cnam-cover__mark" viewBox="0 0 64 64" aria-hidden="true">
                        <path
                          d="M31 12C18 13 9 22 9 34c0 11 8 19 19 20"
                          fill="none"
                          stroke="#3f9b45"
                          strokeWidth="7"
                          strokeLinecap="round"
                        />
                        <circle cx="43" cy="17" r="7.5" fill="#ef9d24" />
                        <path
                          d="M36 32c9 1 15 7 17 15"
                          fill="none"
                          stroke="#f0c62f"
                          strokeWidth="7"
                          strokeLinecap="round"
                        />
                      </svg>

                      <span className="cnam-cover__names">
                        <span className="cnam-cover__ar">الصندوق الوطني للتأمين على المرض</span>
                        <span className="cnam-cover__cnam">CNAM</span>
                        <span className="cnam-cover__fr">
                          Caisse Nationale d&apos;Assurance Maladie
                        </span>
                      </span>
                    </span>

                    <span className="cnam-cover__doc">
                      <span className="cnam-cover__doc-ar">بطاقة علاج</span>
                      <span className="cnam-cover__doc-ar2">نظام استرجاع المصاريف</span>
                      <span className="cnam-cover__doc-fr">
                        SYSTÈME DE
                        <br />
                        REMBOURSEMENT
                      </span>
                    </span>
                  </span>

                  {/* volet recommandations */}
                  <span className="cnam-cover__panel cnam-cover__panel--notes">
                    <span className="cnam-cover__pp">PP</span>
                    <span className="cnam-cover__notes-title">توصيات هامة</span>
                    <span className="cnam-cover__notes">
                      <i>تخول هذه البطاقة لصاحبها ولأفراد عائلته المسجلين بها الحق في :</i>
                      <i>
                        1 - العلاج بالهياكل الصحية العمومية الراجعة بالنظر لوزارة الصحة العمومية
                        والهياكل الصحية العمومية الأخرى المتعاقدة مع الصندوق.
                      </i>
                      <i>
                        2 - العلاج بالقطاعين العمومي والخاص حسب الشروط القانونية والإجراءات الخاصة
                        المنظمة.
                      </i>
                      <i>
                        يجب الاستظهار بهذه البطاقة لدى مسدي الخدمات الصحية المتعاقدين مع الصندوق.
                      </i>
                      <i>
                        كل استعمال أو محاولة استعمال لهذه الوثيقة بدون وجه حق يعرض صاحبها للعقوبات
                        المنصوص عليها بالقانون الجاري به العمل.
                      </i>
                    </span>
                  </span>
                </span>

                <span className="cnam-cover__hint">
                  {isNewCard ? t("cover.hintFill") : t("cover.hintOpen")}
                </span>
              </button>

              {/* ----- INTÉRIEUR DU CARNET ----- */}
              <div className={`cnam-real-book ${fit.compact ? "is-single" : "is-spread"}`}>
                {/* ===== VOLET DROIT (RTL) : ASSURÉ + AYANTS DROIT ===== */}
                <article
                  className={`cnam-page cnam-page--holder ${showHolder ? "" : "is-hidden"}`}
                  aria-label={t("aria.holderPage")}
                >
                  <div className="cnam-page__frame">
                    <header className="cnam-head">
                      <span className="cnam-head__side">صندوق الانخراط</span>
                      <div className="cnam-head__center">
                        <span className="cnam-head__hint">المضمون الاجتماعي</span>
                        <h3 className="cnam-head__logo">CNSS</h3>
                      </div>
                      <span className="cnam-head__side cnam-head__side--fr">F.REMBOURSEMENT</span>
                    </header>

                    <div className="cnam-uid">
                      <span className="cnam-uid__label">المعرف الوحيد</span>
                      <span
                        className="cnam-uid__value"
                        data-ph={t("placeholder.uid")}
                        {...bindEditable(refIdentifiant, readOnly)}
                      />
                    </div>

                    <div className="cnam-rows">
                      <div className="cnam-row">
                        <span className="cnam-row__label">الاسم واللقب :</span>
                        <span className="cnam-line" data-ph={t("placeholder.name")} {...bindEditable(refNomPrenom, readOnly)} />
                      </div>

                      <div className="cnam-row cnam-row--split">
                        <span className="cnam-row__label">تاريخ الولادة :</span>
                        <span
                          className="cnam-line cnam-line--num"
                          data-ph={t("placeholder.date")}
                          {...bindEditable(refDateNaissance, readOnly)}
                        />
                        <span className="cnam-row__label cnam-row__label--sm">رقم ب ت و</span>
                        <span
                          className="cnam-line cnam-line--num"
                          data-ph={t("placeholder.cin")}
                          {...bindEditable(refCin, readOnly)}
                        />
                      </div>

                      <div className="cnam-row">
                        <span className="cnam-row__label">العنوان :</span>
                        <span className="cnam-line" data-ph={t("placeholder.address")} {...bindEditable(refAdresse, readOnly)} />
                      </div>

                      <div className="cnam-row">
                        <span className="cnam-row__label">الصندوق :</span>
                        <span className="cnam-line" data-ph={t("placeholder.caisse")} {...bindEditable(refCaisseName, readOnly)} />
                      </div>
                    </div>

                    <section className="cnam-block">
                      <h4 className="cnam-block__title">القرين</h4>
                      <div className="cnam-block__group">
                        <div className="cnam-row">
                          <span className="cnam-row__label">الاسم واللقب :</span>
                          <span className="cnam-line" data-ph={t("placeholder.spouse")} {...bindEditable(refConjointNom, readOnly)} />
                        </div>
                        <div className="cnam-row cnam-row--split">
                          <span className="cnam-row__label">تاريخ الولادة :</span>
                          <span
                            className="cnam-line cnam-line--num"
                            data-ph={t("placeholder.date")}
                            {...bindEditable(refConjointDate, readOnly)}
                          />
                          <span className="cnam-row__label cnam-row__label--sm">رقم ب ت و</span>
                          <span
                            className="cnam-line cnam-line--num"
                            data-ph={t("placeholder.cin")}
                            {...bindEditable(refConjointCin, readOnly)}
                          />
                        </div>
                      </div>
                    </section>

                    <section className="cnam-block">
                      <h4 className="cnam-block__title">الأصول في الكفالة</h4>
                      {refAsc.current.map((asc, i) => (
                        <div className="cnam-block__group" key={`asc-${i}`}>
                          <div className="cnam-row">
                            <span className="cnam-row__label">الاسم واللقب :</span>
                            <span
                              className="cnam-line"
                              data-ph={t("placeholder.ascendant", { index: i + 1 })}
                              {...bindEditable(asc.nom, readOnly)}
                            />
                          </div>
                          <div className="cnam-row cnam-row--split">
                            <span className="cnam-row__label">تاريخ الولادة :</span>
                            <span
                              className="cnam-line cnam-line--num"
                              data-ph={t("placeholder.date")}
                              {...bindEditable(asc.date, readOnly)}
                            />
                            <span className="cnam-row__label cnam-row__label--sm">رقم ب ت و</span>
                            <span
                              className="cnam-line cnam-line--num"
                              data-ph={t("placeholder.cin")}
                              {...bindEditable(asc.cin, readOnly)}
                            />
                          </div>
                        </div>
                      ))}
                    </section>
                  </div>
                </article>

                {/* ===== VOLET GAUCHE (RTL) : ENFANTS À CHARGE ===== */}
                <article
                  className={`cnam-page cnam-page--children ${showChildren ? "" : "is-hidden"}`}
                  aria-label={t("aria.childrenPage")}
                >
                  <div className="cnam-page__frame">
                    <div className="cnam-validity">
                      <span className="cnam-validity__label">صالحة من :</span>
                      <span
                        className="cnam-validity__value"
                        data-ph={t("placeholder.date")}
                        {...bindEditable(refValidFrom, readOnly)}
                      />
                      <span className="cnam-validity__label">إلى</span>
                      <span
                        className="cnam-validity__value"
                        data-ph={t("placeholder.date")}
                        {...bindEditable(refValidTo, readOnly)}
                      />
                    </div>

                    <div className="cnam-kids">
                      <div className="cnam-kids__title">الأبناء في الكفالة</div>
                      <table className="cnam-table">
                        <thead>
                          <tr>
                            <th>الاسم</th>
                            <th>تاريخ الولادة</th>
                            <th>نهاية التحملية</th>
                          </tr>
                        </thead>
                        <tbody>
                          {refEnfants.current.map((e, i) => (
                            <tr key={`enfant-${i}`}>
                              <td {...bindEditable(e.nom, readOnly)} />
                              <td {...bindEditable(e.date, readOnly)} />
                              <td {...bindEditable(e.fin, readOnly)} />
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    <div className="cnam-agreement">
                      <p className="cnam-agreement__title">الاتفاقية الثنائية</p>
                      <p>التسجيل بمؤسسة التأمين الأجنبية</p>
                      <p>الختم والإمضاء</p>

                      {!isNewCard && (
                        <>
                          <div className="cnam-stamp" aria-hidden="true">
                            <span>الصندوق الوطني للتأمين على المرض</span>
                            <span>CNAM — Centre Local</span>
                            <span>Matricule</span>
                          </div>
                          <svg className="cnam-sign" viewBox="0 0 120 40" fill="none" aria-hidden="true">
                            <path
                              d="M6 30c9-3 12-19 19-21s6 19 12 21 10-15 17-17 5 14 11 16 12-7 18-11 14-4 19-2"
                              stroke="currentColor"
                              strokeWidth="2.2"
                              strokeLinecap="round"
                            />
                          </svg>
                        </>
                      )}

                      <div className="cnam-edited">
                        Édité le : <span ref={refSignature} />
                      </div>
                    </div>
                  </div>
                </article>
              </div>
            </div>
          </div>
        </div>

        {/* ---------- ACTIONS ---------- */}
        <div className="cnam-save-bar">
          <div className="cnam-save-actions">
            {isOpenBook ? (
              <>
                <Button
                  className="cnam-ghost-btn"
                  disabled={saving}
                  onClick={() => setIsOpenBook(false)}
                >
                  {t("actions.cover")}
                </Button>
                {!readOnly && !hideSaveButton && (
                  <Button
                    type="primary"
                    loading={saving}
                    disabled={saving}
                    onClick={handleSave}
                    className="cnam-save-btn"
                  >
                    {saving ? t("actions.saving") : isNewCard ? t("actions.saveNew") : t("actions.save")}
                  </Button>
                )}
              </>
            ) : (
              <>
                <Button className="cnam-ghost-btn" onClick={onClose}>
                  {t("actions.close")}
                </Button>
                <Button
                  type="primary"
                  className="cnam-save-btn"
                  onClick={() => setIsOpenBook(true)}
                >
                  {readOnly ? t("actions.open") : isNewCard ? t("actions.fill") : t("actions.open")}
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
}
